// Execution engine that generates step-by-step execution traces for each algorithm

import {
  generateBinarySearchSteps,
  generateSearchRotatedSteps,
  generateFindPeakSteps,
  generateBubbleSortSteps,
  generateQuickSortSteps,
  generateMergeSortSteps
} from './searchSortEngine';

import {
  generateFactorialSteps,
  generateFibonacciSteps,
  generateTowerOfHanoiSteps,
  generateSubsetsSteps,
  generatePermutationsSteps,
  generateNQueensSteps
} from './recursionBacktrackEngine';

import {
  generateFibonacciMemoSteps,
  generateLCSSteps,
  generateKnapsackSteps,
  generateBFSSteps,
  generateDFSSteps,
  generateDijkstraSteps
} from './dpGraphEngine';

import {
  generateInorderSteps,
  generateLevelOrderSteps,
  generateLCASteps,
  generateNaiveMatchSteps,
  generateKMPSteps,
  generateRabinKarpSteps
} from './treeStringEngine';

export const generateLinearSearchSteps = (array, target) => {
  const steps = [];
  
  for (let i = 0; i < array.length; i++) {
    steps.push({
      line: 2,
      description: `Starting iteration ${i + 1}`,
      variables: { i, target, arrayLength: array.length },
      currentIndex: i,
      highlight: [i],
      comparison: null
    });
    
    steps.push({
      line: 3,
      description: `Checking if array[${i}] (${array[i]}) equals target (${target})`,
      variables: { i, target, arrayLength: array.length, currentValue: array[i] },
      currentIndex: i,
      highlight: [i],
      comparison: { index: i, value: array[i], matches: array[i] === target }
    });
    
    if (array[i] === target) {
      steps.push({
        line: 4,
        description: `Found target ${target} at index ${i}`,
        variables: { i, target, result: i },
        currentIndex: i,
        highlight: [i],
        found: true,
        result: i
      });
      return steps;
    }
  }
  
  steps.push({
    line: 7,
    description: `Target ${target} not found in array`,
    variables: { target, result: -1 },
    currentIndex: -1,
    highlight: [],
    found: false,
    result: -1
  });
  
  return steps;
};

export const generateProductExceptSelfSteps = (array) => {
  const steps = [];
  const n = array.length;
  const result = new Array(n);
  
  // Build prefix products
  result[0] = 1;
  steps.push({
    line: 4,
    description: `Initialize result[0] = 1 (no elements before index 0)`,
    variables: { n, i: 0 },
    currentIndex: 0,
    result: [...result],
    phase: 'prefix'
  });
  
  for (let i = 1; i < n; i++) {
    result[i] = result[i - 1] * array[i - 1];
    steps.push({
      line: 5,
      description: `result[${i}] = result[${i-1}] * nums[${i-1}] = ${result[i-1]} * ${array[i-1]} = ${result[i]}`,
      variables: { n, i, prevResult: result[i-1], prevNum: array[i-1] },
      currentIndex: i,
      result: [...result],
      phase: 'prefix'
    });
  }
  
  // Build suffix products
  let suffix = 1;
  steps.push({
    line: 9,
    description: `Starting suffix calculation, suffix = 1`,
    variables: { n, suffix },
    currentIndex: n - 1,
    result: [...result],
    phase: 'suffix'
  });
  
  for (let i = n - 1; i >= 0; i--) {
    const oldResult = result[i];
    result[i] = result[i] * suffix;
    steps.push({
      line: 10,
      description: `result[${i}] = ${oldResult} * suffix(${suffix}) = ${result[i]}`,
      variables: { n, i, suffix, oldResult },
      currentIndex: i,
      result: [...result],
      phase: 'suffix'
    });
    
    suffix *= array[i];
    steps.push({
      line: 11,
      description: `Update suffix: ${suffix / array[i]} * nums[${i}](${array[i]}) = ${suffix}`,
      variables: { n, i, suffix },
      currentIndex: i,
      result: [...result],
      phase: 'suffix'
    });
  }
  
  steps.push({
    line: 13,
    description: `Algorithm complete. Final result array computed.`,
    variables: { result: [...result] },
    currentIndex: -1,
    result: [...result],
    phase: 'complete'
  });
  
  return steps;
};

export const generateFirstMissingPositiveSteps = (array) => {
  const steps = [];
  const nums = [...array];
  const n = nums.length;
  
  // Phase 1: Place numbers in correct positions
  for (let i = 0; i < n; i++) {
    steps.push({
      line: 4,
      description: `Checking position ${i}, value: ${nums[i]}`,
      variables: { n, i, currentValue: nums[i] },
      currentIndex: i,
      array: [...nums],
      phase: 'placement'
    });
    
    while (nums[i] > 0 && nums[i] <= n && nums[nums[i] - 1] !== nums[i]) {
      const targetIndex = nums[i] - 1;
      const temp = nums[targetIndex];
      
      steps.push({
        line: 5,
        description: `Swapping nums[${i}](${nums[i]}) to correct position ${targetIndex}`,
        variables: { i, targetIndex, swapFrom: nums[i], swapTo: temp },
        currentIndex: i,
        targetIndex: targetIndex,
        array: [...nums],
        phase: 'placement',
        swap: true
      });
      
      nums[targetIndex] = nums[i];
      nums[i] = temp;
      
      steps.push({
        line: 6,
        description: `After swap: nums[${i}] = ${nums[i]}, nums[${targetIndex}] = ${nums[targetIndex]}`,
        variables: { i, targetIndex },
        currentIndex: i,
        array: [...nums],
        phase: 'placement'
      });
    }
  }
  
  // Phase 2: Find first missing
  for (let i = 0; i < n; i++) {
    steps.push({
      line: 11,
      description: `Checking if nums[${i}] (${nums[i]}) equals ${i + 1}`,
      variables: { i, expected: i + 1, actual: nums[i] },
      currentIndex: i,
      array: [...nums],
      phase: 'finding'
    });
    
    if (nums[i] !== i + 1) {
      steps.push({
        line: 12,
        description: `First missing positive found: ${i + 1}`,
        variables: { result: i + 1 },
        currentIndex: i,
        array: [...nums],
        phase: 'complete',
        result: i + 1
      });
      return steps;
    }
  }
  
  steps.push({
    line: 16,
    description: `All positions filled correctly. Missing: ${n + 1}`,
    variables: { result: n + 1 },
    currentIndex: -1,
    array: [...nums],
    phase: 'complete',
    result: n + 1
  });
  
  return steps;
};

export const generateRemoveDuplicatesSteps = (array) => {
  const steps = [];
  const nums = [...array];
  
  if (nums.length === 0) {
    steps.push({
      line: 2,
      description: 'Empty array, return 0',
      variables: { result: 0 },
      result: 0
    });
    return steps;
  }
  
  let slow = 0;
  
  steps.push({
    line: 4,
    description: 'Initialize slow pointer at index 0',
    variables: { slow },
    slow: 0,
    fast: 0,
    array: [...nums]
  });
  
  for (let fast = 1; fast < nums.length; fast++) {
    steps.push({
      line: 6,
      description: `Fast pointer at ${fast}, comparing nums[${fast}](${nums[fast]}) with nums[${slow}](${nums[slow]})`,
      variables: { slow, fast, fastValue: nums[fast], slowValue: nums[slow] },
      slow: slow,
      fast: fast,
      array: [...nums],
      comparison: nums[fast] !== nums[slow]
    });
    
    if (nums[fast] !== nums[slow]) {
      slow++;
      steps.push({
        line: 7,
        description: `Found new unique value. Move slow to ${slow}`,
        variables: { slow, fast },
        slow: slow,
        fast: fast,
        array: [...nums]
      });
      
      nums[slow] = nums[fast];
      steps.push({
        line: 8,
        description: `Copy nums[${fast}](${nums[fast]}) to nums[${slow}]`,
        variables: { slow, fast, copiedValue: nums[fast] },
        slow: slow,
        fast: fast,
        array: [...nums]
      });
    }
  }
  
  const result = slow + 1;
  steps.push({
    line: 12,
    description: `Algorithm complete. Unique elements: ${result}`,
    variables: { slow, result },
    slow: slow,
    fast: nums.length,
    array: [...nums],
    result: result
  });
  
  return steps;
};

export const generateMaxAreaSteps = (heights) => {
  const steps = [];
  let left = 0;
  let right = heights.length - 1;
  let maxArea = 0;
  
  steps.push({
    line: 2,
    description: 'Initialize left and right pointers at array ends',
    variables: { left, right, maxArea },
    left,
    right,
    heights: [...heights]
  });
  
  while (left < right) {
    const width = right - left;
    const h = Math.min(heights[left], heights[right]);
    const area = width * h;
    
    steps.push({
      line: 6,
      description: `Calculate area: width(${width}) * min_height(${h}) = ${area}`,
      variables: { left, right, width, height: h, area, maxArea, leftHeight: heights[left], rightHeight: heights[right] },
      left,
      right,
      heights: [...heights],
      calculation: { width, height: h, area }
    });
    
    maxArea = Math.max(maxArea, area);
    
    steps.push({
      line: 7,
      description: `Current max area: ${maxArea}`,
      variables: { left, right, maxArea },
      left,
      right,
      heights: [...heights],
      maxArea
    });
    
    if (heights[left] < heights[right]) {
      steps.push({
        line: 9,
        description: `height[${left}](${heights[left]}) < height[${right}](${heights[right]}), move left pointer`,
        variables: { left, right, leftHeight: heights[left], rightHeight: heights[right] },
        left,
        right,
        heights: [...heights],
        decision: 'move-left'
      });
      left++;
    } else {
      steps.push({
        line: 11,
        description: `height[${left}](${heights[left]}) >= height[${right}](${heights[right]}), move right pointer`,
        variables: { left, right, leftHeight: heights[left], rightHeight: heights[right] },
        left,
        right,
        heights: [...heights],
        decision: 'move-right'
      });
      right--;
    }
  }
  
  steps.push({
    line: 15,
    description: `Pointers met. Maximum area: ${maxArea}`,
    variables: { maxArea, result: maxArea },
    left,
    right,
    heights: [...heights],
    result: maxArea
  });
  
  return steps;
};

export const generateTrapRainWaterSteps = (heights) => {
  const steps = [];
  let left = 0;
  let right = heights.length - 1;
  let leftMax = 0;
  let rightMax = 0;
  let water = 0;
  
  steps.push({
    line: 2,
    description: 'Initialize pointers and max heights',
    variables: { left, right, leftMax, rightMax, water },
    left,
    right,
    heights: [...heights],
    water: 0
  });
  
  while (left < right) {
    steps.push({
      line: 5,
      description: `Comparing height[${left}](${heights[left]}) and height[${right}](${heights[right]})`,
      variables: { left, right, leftHeight: heights[left], rightHeight: heights[right], leftMax, rightMax, water },
      left,
      right,
      heights: [...heights],
      water
    });
    
    if (heights[left] < heights[right]) {
      if (heights[left] >= leftMax) {
        leftMax = heights[left];
        steps.push({
          line: 6,
          description: `Update leftMax to ${leftMax}`,
          variables: { left, leftMax, water },
          left,
          right,
          heights: [...heights],
          water
        });
      } else {
        const trapped = leftMax - heights[left];
        water += trapped;
        steps.push({
          line: 8,
          description: `Trap ${trapped} units at index ${left}. Total water: ${water}`,
          variables: { left, leftMax, currentHeight: heights[left], trapped, water },
          left,
          right,
          heights: [...heights],
          water,
          trapped: { index: left, amount: trapped }
        });
      }
      left++;
    } else {
      if (heights[right] >= rightMax) {
        rightMax = heights[right];
        steps.push({
          line: 12,
          description: `Update rightMax to ${rightMax}`,
          variables: { right, rightMax, water },
          left,
          right,
          heights: [...heights],
          water
        });
      } else {
        const trapped = rightMax - heights[right];
        water += trapped;
        steps.push({
          line: 14,
          description: `Trap ${trapped} units at index ${right}. Total water: ${water}`,
          variables: { right, rightMax, currentHeight: heights[right], trapped, water },
          left,
          right,
          heights: [...heights],
          water,
          trapped: { index: right, amount: trapped }
        });
      }
      right--;
    }
  }
  
  steps.push({
    line: 19,
    description: `Algorithm complete. Total water trapped: ${water}`,
    variables: { water, result: water },
    left,
    right,
    heights: [...heights],
    water,
    result: water
  });
  
  return steps;
};

export const getExecutionSteps = (lessonId, array, target) => {
  switch (lessonId) {
    case 'linear-search':
      return generateLinearSearchSteps(array, target);
    case 'product-except-self':
      return generateProductExceptSelfSteps(array);
    case 'first-missing-positive':
      return generateFirstMissingPositiveSteps(array);
    case 'remove-duplicates':
      return generateRemoveDuplicatesSteps(array);
    case 'container-with-most-water':
      return generateMaxAreaSteps(array);
    case 'trapping-rain-water':
      return generateTrapRainWaterSteps(array);
    case 'binary-search':
      return generateBinarySearchSteps(array, target);
    case 'search-rotated':
      return generateSearchRotatedSteps(array, target);
    case 'find-peak-element':
      return generateFindPeakSteps(array);
    case 'bubble-sort':
      return generateBubbleSortSteps(array);
    case 'quick-sort':
      return generateQuickSortSteps(array);
    case 'merge-sort':
      return generateMergeSortSteps(array);
    case 'factorial':
      return generateFactorialSteps(array[0]);
    case 'fibonacci':
      return generateFibonacciSteps(array[0]);
    case 'tower-of-hanoi':
      return generateTowerOfHanoiSteps(array[0]);
    case 'subsets':
      return generateSubsetsSteps(array);
    case 'permutations':
      return generatePermutationsSteps(array);
    case 'n-queens':
      return generateNQueensSteps(array[0]);
    case 'fibonacci-memo':
      return generateFibonacciMemoSteps(array[0]);
    case 'lcs':
      return generateLCSSteps(array);
    case 'knapsack':
      return generateKnapsackSteps(array);
    case 'bfs':
      return generateBFSSteps(array[0]);
    case 'dfs':
      return generateDFSSteps(array[0]);
    case 'dijkstra':
      return generateDijkstraSteps(array[0]);
    case 'inorder-traversal':
      return generateInorderSteps(array);
    case 'level-order-traversal':
      return generateLevelOrderSteps(array);
    case 'lca':
      return generateLCASteps(array);
    case 'naive-string-match':
      return generateNaiveMatchSteps(array);
    case 'kmp':
      return generateKMPSteps(array);
    case 'rabin-karp':
      return generateRabinKarpSteps(array);
    default:
      return [];
  }
};
